import type {
  MemberAccountInformation,
  MemberAccountInformationRequest
} from '@library/types/members';
import type { ApiError } from '@bo-types/authentication';
import { convertToYearMonthDayFormat } from '@library/utils';
import { useCallback } from 'react';
import { useUpdateMemberMutation } from '@bo-services/members/membersApi';

export interface MemberAccountInformationProps {
  memberId: string;
}

export const useMemberAccountInformation = ({
  memberId
}: MemberAccountInformationProps) => {
  const [
    updateMember,
    { error: memberInfoError, isError, isLoading, isSuccess, isUninitialized }
  ] = useUpdateMemberMutation();

  const handleSubmit = useCallback(
    async (accountInformation: Partial<MemberAccountInformation>) => {
      if (!memberId) {
        throw new Error('Member ID is required');
      }

      const {
        address,
        city,
        dateOfBirth,
        email,
        firstName,
        inviteCode,
        lastName,
        phoneNumber,
        postalCode,
        state,
        username,
        verified
      } = accountInformation;

      const dateOfBirthFormat = convertToYearMonthDayFormat(dateOfBirth ?? '');
      const formattedPhoneNumber = phoneNumber?.replace(/\D/g, '') ?? null;

      const accountInfoRequest: MemberAccountInformationRequest = {
        address,
        city,
        ...(dateOfBirth && { date_of_birth: dateOfBirthFormat }),
        email,
        first_name: firstName,
        invite_code: inviteCode,
        last_name: lastName,
        ...(phoneNumber !== undefined && {
          phone_number: formattedPhoneNumber
        }),
        postal_code: postalCode,
        state,
        username,
        verified
      };

      const hasValidAccountInfoFields = Object.values(accountInfoRequest).some(
        (value) => value !== undefined
      );

      if (hasValidAccountInfoFields) {
        await updateMember({
          member_id: memberId,
          ...accountInfoRequest
        }).unwrap();
      }
    },
    [memberId, updateMember]
  );

  const errorMessage = (memberInfoError as ApiError)?.data?.error;

  return {
    errorMessage,
    isError,
    isLoading,
    isSuccess,
    isUninitialized,
    onSubmit: handleSubmit
  };
};
